import type { GeoProjection } from "d3-geo";

/**
 * The photograph of Earth the globe is painted from, and the painter that wraps
 * it around the sphere.
 *
 * The rest of the globe is SVG driven by d3's orthographic projection, which can
 * place geometry on the sphere but cannot put a picture on it — so the surface
 * goes onto a canvas of its own underneath the SVG, and this module fills it by
 * running the projection backwards: for every pixel of the canvas, work out
 * which point of the planet is facing the viewer there and take its colour out
 * of the texture.
 */

/**
 * NASA Blue Marble Next Generation, topography and bathymetry, downscaled to
 * 2048x1024 WebP. Served out of `public/geo` next to the country outlines and
 * the city lights rather than bundled, on the same terms as those.
 */
export const EARTH_TEXTURE_URL = "/geo/earth-relief.webp";

/* -------------------------------------------------------------------------- */
/* The night grade                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The source is a *daylight* photograph — green and tan continents on bright
 * blue sea. Dropped onto the globe as it comes it reads as a school atlas, so
 * every texel is regraded once on load into the palette this section was drawn
 * in. Doing it here rather than in the asset keeps the numbers below tunable
 * against the reference without regenerating the image.
 *
 * The constants are all in play when matching the reference. Broadly: the sea
 * goes almost black, the land is stripped of its own colour and retinted cool,
 * and the top end is compressed so that nothing on the surface can ever
 * out-shine the city lights that are drawn over it.
 */

/** Sea is strongly blue-dominant in the source; land, desert and snow are not. */
const OCEAN_BLUE_OVER_RED = 1.35;
const OCEAN_BLUE_OVER_GREEN = 1.1;

/**
 * The sea is the darkest thing in frame, but it is still *sea* — a deep navy
 * rather than a hole cut in the picture. Taken all the way to black it stops
 * reading as water and the sphere loses its middle, so the floor here sits just
 * high enough to keep a blue in it. The gap to `LAND_SHADOW` below is what makes
 * the continents read at a glance.
 */
const OCEAN_DEEP: [number, number, number] = [5, 16, 40];
const OCEAN_SHALLOW: [number, number, number] = [11, 31, 71];
/**
 * How much of the sea's own variation survives the crush. Enough that the
 * continental shelves are there to be found when the sphere zooms in, little
 * enough that at rest the ocean reads as one dark mass.
 */
const OCEAN_VARIATION = 0.35;
/** Sea occupies only the bottom of the luminance range — stretch it over that. */
const OCEAN_LUMINANCE_RANGE = 0.32;

/**
 * Land runs between these two: a deep blue in shadow up to a strong, saturated
 * one in the light, both well short of white.
 *
 * The instinct is to desaturate the photograph towards grey and let the section
 * supply the colour. The reference does the opposite — the continents there are
 * emphatically *blue*, and brighter than a first pass at "night" would ever make
 * them. A neutral grey-blue reads as a dimmed day map; this reads as a planet
 * lit by a blue atmosphere.
 */
const LAND_SHADOW: [number, number, number] = [17, 40, 84];
const LAND_HIGHLIGHT: [number, number, number] = [66, 112, 186];
/**
 * Applied to luminance before the retint. Held near 1 so the ramp stays close to
 * linear: mountain, desert and plain are the whole reason for using a
 * photograph, and a steep curve trades them away for contrast at the top.
 */
const LAND_GAMMA = 1.05;
/**
 * Above the knee the ramp all but stops. December in the northern hemisphere,
 * so the source carries a great deal of snow, and snow is the one thing bright
 * enough to compete with the lights if it is left alone.
 */
const LAND_KNEE = 0.62;
const LAND_KNEE_SLOPE = 0.32;

/* -------------------------------------------------------------------------- */
/* Painting                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * How much darker the sphere goes towards its edge. A real planet does this
 * because you are looking through more of it side-on; without it an
 * orthographic globe reads as a sticker of a map cut into a circle.
 */
const LIMB_DARKENING = 0.42;

/**
 * Width of the fade across the limb, in rendered pixels. The disc is solved per
 * row, so without this the sphere ends on an exact hard boundary and the edge
 * comes out as visible stair-steps — the single thing that most makes the globe
 * read as a cheap image. Carried in pixels rather than radii so it stays the
 * same apparent softness however far the sphere has zoomed.
 */
const EDGE_FEATHER_PIXELS = 1.6;

const TAU = Math.PI * 2;
const HALF_PI = Math.PI / 2;
const DEGREES = Math.PI / 180;

export type EarthTexture = {
  /** Graded RGBA, row-major from the north-west corner. */
  data: Uint8ClampedArray;
  width: number;
  height: number;
};

/** The stage the canvas is stretched over, in viewBox units and fractions. */
export type EarthStage = {
  width: number;
  height: number;
  overscanX: number;
  overscanY: number;
};

/** Where the light falls, in radii from the sphere's centre, and how hard. */
export type EarthLight = {
  offsetX: number;
  offsetY: number;
  falloff: number;
};

function mix(from: number, to: number, amount: number) {
  return from + (to - from) * amount;
}

/**
 * Get the image in a form `drawImage` will take.
 *
 * `HTMLImageElement.decode()` is the obvious way to do this and it does not
 * work here: against this file, in Chrome, the promise it hands back simply
 * never settles — no error, no rejection, the texture just never arrives and
 * the globe silently keeps its fallback. `createImageBitmap` decodes the same
 * bytes in a few tens of milliseconds and hands back something that can be
 * drawn straight off. The load event is kept behind it for anything that has no
 * `createImageBitmap`, which is the same path the browser would have taken
 * anyway.
 */
async function loadImage(url: string): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    const response = await fetch(url);

    if (!response.ok) throw new Error(`earth texture: ${response.status}`);

    return createImageBitmap(await response.blob());
  }

  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("earth texture: load failed"));
    image.src = url;
  });
}

/**
 * Fetch the texture and regrade it in place, once. Every frame after this is a
 * lookup, which is the only reason the per-pixel pass below can afford to run at
 * all.
 */
export async function loadEarthTexture(
  url = EARTH_TEXTURE_URL,
): Promise<EarthTexture> {
  const source = await loadImage(url);
  const width =
    source instanceof ImageBitmap ? source.width : source.naturalWidth;
  const height =
    source instanceof ImageBitmap ? source.height : source.naturalHeight;
  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { willReadFrequently: true });

  if (!context) throw new Error("earth texture: no 2d context");

  context.drawImage(source, 0, 0);

  if (source instanceof ImageBitmap) source.close();

  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;

  for (let offset = 0; offset < data.length; offset += 4) {
    const red = data[offset];
    const green = data[offset + 1];
    const blue = data[offset + 2];

    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

    let target: [number, number, number];
    let amount: number;

    if (
      blue > red * OCEAN_BLUE_OVER_RED &&
      blue > green * OCEAN_BLUE_OVER_GREEN
    ) {
      const depth = Math.min(1, luminance / OCEAN_LUMINANCE_RANGE);

      target = OCEAN_SHALLOW;
      amount = depth * OCEAN_VARIATION;
      data[offset] = mix(OCEAN_DEEP[0], target[0], amount);
      data[offset + 1] = mix(OCEAN_DEEP[1], target[1], amount);
      data[offset + 2] = mix(OCEAN_DEEP[2], target[2], amount);
      continue;
    }

    let ramp = Math.pow(Math.min(1, luminance), LAND_GAMMA);

    if (ramp > LAND_KNEE) {
      ramp = LAND_KNEE + (ramp - LAND_KNEE) * LAND_KNEE_SLOPE;
    }

    data[offset] = mix(LAND_SHADOW[0], LAND_HIGHLIGHT[0], ramp);
    data[offset + 1] = mix(LAND_SHADOW[1], LAND_HIGHLIGHT[1], ramp);
    data[offset + 2] = mix(LAND_SHADOW[2], LAND_HIGHLIGHT[2], ramp);
  }

  return { data, width, height };
}

export type EarthPainter = {
  paint: (options: {
    canvas: HTMLCanvasElement;
    projection: GeoProjection;
    texture: EarthTexture;
    stage: EarthStage;
    light: EarthLight;
    /** Roughly how many pixels this pass may cost. Lower it while moving. */
    budget: number;
  }) => void;
};

/**
 * Holds the buffer the sphere is drawn into between frames, so a rotation
 * allocates nothing.
 *
 * The sphere is painted at a fraction of the canvas it ends up on and scaled
 * back up. That is affordable here in a way it would not be for the city
 * lights — a night Earth is all soft gradient, so the resampling has almost
 * nothing to blur, whereas a light has a hard core and losing it is exactly what
 * reads as pixellation. The fraction comes from `budget`, which the hook drops
 * while the sphere is in motion and restores on the frame it settles.
 */
export function createEarthPainter(): EarthPainter {
  const scratch = document.createElement("canvas");
  const scratchContext = scratch.getContext("2d");

  let buffer: ImageData | null = null;

  return {
    paint({ canvas, projection, texture, stage, light, budget }) {
      const context = canvas.getContext("2d");

      if (!context || !scratchContext) return;
      if (!canvas.width || !canvas.clientWidth) return;

      // Unpick the overscan to recover the box the viewBox units map onto —
      // the same reasoning, and the same numbers, as the city lights use.
      const boxWidth = canvas.clientWidth / (1 + stage.overscanX * 2);
      const ratio = canvas.width / canvas.clientWidth;
      const unit = (boxWidth / stage.width) * ratio;

      if (!unit) return;

      const originX = stage.overscanX * stage.width;
      const originY = stage.overscanY * stage.height;

      const scale = projection.scale();

      // Budget the pass by the pixels the sphere actually covers, not by the
      // size of the canvas. The two are wildly different things here: at rest
      // the disc is a seventh of an oversized canvas and rows that miss it are
      // skipped whole, so a canvas-sized budget throws away resolution to pay
      // for pixels that are never touched — which is what leaves the limb soft
      // and stepped. Zoomed in the sphere fills the canvas and the same budget
      // pulls the resolution back down. Cost stays flat either way.
      const radiusInPixels = scale * unit;
      const covered = Math.min(
        canvas.width * canvas.height,
        Math.PI * radiusInPixels * radiusInPixels,
      );
      const quality = covered > 0 ? Math.min(1, Math.sqrt(budget / covered)) : 1;
      const renderWidth = Math.max(1, Math.round(canvas.width * quality));
      const renderHeight = Math.max(1, Math.round(canvas.height * quality));

      if (scratch.width !== renderWidth || scratch.height !== renderHeight) {
        scratch.width = renderWidth;
        scratch.height = renderHeight;
        buffer = null;
      }

      if (!buffer) {
        buffer = scratchContext.createImageData(renderWidth, renderHeight);
      }

      const pixels = buffer.data;

      pixels.fill(0);

      const [translateX, translateY] = projection.translate();
      const [rotateLambda, rotatePhi, rotateGamma] = projection.rotate();

      const cosLambda = Math.cos(rotateLambda * DEGREES);
      const sinLambda = Math.sin(rotateLambda * DEGREES);
      const cosPhi = Math.cos(rotatePhi * DEGREES);
      const sinPhi = Math.sin(rotatePhi * DEGREES);
      const cosGamma = Math.cos(rotateGamma * DEGREES);
      const sinGamma = Math.sin(rotateGamma * DEGREES);

      // One render pixel is this many viewBox units, and the sphere is a unit
      // one — so step across it in radii and the projection's scale drops out
      // of the inner loop entirely.
      const step = 1 / (unit * quality);
      const horizontalStep = step / scale;
      const horizontalBase = (-originX - translateX) / scale;
      const verticalStep = -step / scale;
      const verticalBase = (originY + translateY) / scale;

      const { data: texels, width: textureWidth, height: textureHeight } =
        texture;
      const lastColumn = textureWidth - 1;
      const lastRow = textureHeight - 1;

      // The fade across the limb, carried in the same units as `radius` below —
      // which is the *square* of the distance from centre. Near the edge that
      // squaring doubles the rate of change, hence the factor of two, so the
      // feather comes out the width it was asked for rather than half of it.
      const featherInRadii = EDGE_FEATHER_PIXELS * Math.abs(horizontalStep);
      const feather = Math.max(1e-6, 2 * featherInRadii);

      for (let row = 0; row < renderHeight; row++) {
        // `up` is the pixel's height above the sphere's centre, in radii.
        const up = verticalBase + row * verticalStep;
        const upSquared = up * up;

        if (upSquared >= 1) continue; // this whole row misses the sphere

        // Solve the disc for this row rather than testing every pixel against
        // it — off-sphere pixels then cost nothing at all, which is what makes
        // the resting globe cheap however far the canvas is oversized.
        const halfWidth = Math.sqrt(1 - upSquared);
        const from = Math.max(
          0,
          Math.ceil((-halfWidth - horizontalBase) / horizontalStep),
        );
        const to = Math.min(
          renderWidth - 1,
          Math.floor((halfWidth - horizontalBase) / horizontalStep),
        );

        for (let column = from; column <= to; column++) {
          const right = horizontalBase + column * horizontalStep;
          const radius = right * right + upSquared;

          if (radius >= 1) continue;

          // Towards the viewer. The camera frame is the one the city lights
          // work in — x out of the screen, y right, z up.
          const towards = Math.sqrt(1 - radius);

          // Undo the projection's rotation: the transpose of the spin-then-tilt
          // the lights apply going the other way.
          const spunY = right * cosGamma + up * sinGamma;
          const tilted = up * cosGamma - right * sinGamma;
          const spunX = towards * cosPhi + tilted * sinPhi;
          const polar = tilted * cosPhi - towards * sinPhi;
          const a = spunX * cosLambda + spunY * sinLambda;
          const b = spunY * cosLambda - spunX * sinLambda;

          const longitude = Math.atan2(b, a);
          const latitude = Math.asin(polar < -1 ? -1 : polar > 1 ? 1 : polar);

          let textureX = ((longitude + Math.PI) / TAU) * textureWidth - 0.5;
          let textureY = ((HALF_PI - latitude) / Math.PI) * textureHeight - 0.5;

          textureX =
            textureX < 0 ? 0 : textureX > lastColumn ? lastColumn : textureX;
          textureY = textureY < 0 ? 0 : textureY > lastRow ? lastRow : textureY;

          // Blend the four texels around the point rather than snapping to the
          // nearest. Snapping is cheaper and it is what makes the surface crawl
          // and sparkle as the sphere turns — neighbouring screen pixels jump
          // between texels instead of sliding between them, and on a photograph
          // of coastlines that reads as a low-quality image rather than as
          // detail.
          const westColumn = textureX | 0;
          const northRow = textureY | 0;
          const eastColumn =
            westColumn < lastColumn ? westColumn + 1 : westColumn;
          const southRow = northRow < lastRow ? northRow + 1 : northRow;
          const fractionX = textureX - westColumn;
          const fractionY = textureY - northRow;

          const northWest = (northRow * textureWidth + westColumn) << 2;
          const northEast = (northRow * textureWidth + eastColumn) << 2;
          const southWest = (southRow * textureWidth + westColumn) << 2;
          const southEast = (southRow * textureWidth + eastColumn) << 2;

          // Fall away from the light the way every other layer does, then darken
          // towards the edge so the disc reads as a ball.
          const distance = Math.min(
            1,
            Math.hypot(right - light.offsetX, -up - light.offsetY),
          );
          const shade =
            (1 - light.falloff * distance) * (1 - LIMB_DARKENING * (1 - towards));

          const target = (row * renderWidth + column) << 2;

          for (let channel = 0; channel < 3; channel++) {
            const upper =
              texels[northWest + channel] +
              (texels[northEast + channel] - texels[northWest + channel]) *
                fractionX;
            const lower =
              texels[southWest + channel] +
              (texels[southEast + channel] - texels[southWest + channel]) *
                fractionX;

            pixels[target + channel] = (upper + (lower - upper) * fractionY) *
              shade;
          }

          const edge = (1 - radius) / feather;

          pixels[target + 3] = edge >= 1 ? 255 : edge * 255;
        }
      }

      scratchContext.putImageData(buffer, 0, 0);

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(scratch, 0, 0, canvas.width, canvas.height);
    },
  };
}
